// src/config/menu.js
// サイドバーメニューの定義と、ロール / 機能フラグでのフィルタリング。
//
// MENU 配列の各エントリは以下のフィールドを持つ:
//   to:       ルートパス
//   icon:     components/Icon.jsx のアイコン名（絵文字はOS毎に見た目が違うため廃止【2026-07-21】）
//   label:    表示名
//   roles:    表示するロール一覧 (必須)
//   feature:  オプション。指定するとユーザーの features 配列にこの値が無いと出さない
//             user.features が undefined or null の場合は「全機能ON」とみなす（後方互換）
//
// 通知 (/notifications) と アカウント設定 (/profile) は topbar から開く前提なので
// サイドバーには出さないが、ページタイトル解決のために HIDDEN_TOP_PAGES に登録しておく。

const MENU = [
  { to: '/',             icon: 'home', label: 'ダッシュボード',  roles: ['student','teacher','academic_staff','developer'], tab: true },
  // 【2026-11-02】要対応：申請・確認待ちの打刻・要注意のお知らせ・統合候補を1か所に（pages/ActionItems.jsx）。
  //   badge … サイドバーに件数を出す（hooks/useActionCount.js）
  { to: '/todo',         icon: 'alert-triangle', label: '要対応', roles: ['teacher','academic_staff'], badge: 'todo' },
  { to: '/attendance',   icon: 'check-square', label: '出席確認',         roles: ['student'] },
  // 【2026-10-16】メッセージ機能。msgFeature はメッセージ機能のスイッチ（/api/messages/features）。
  //   スイッチが全部 OFF の間は出さない（本番で順に開けるため。開発者は常に ON）。
  //   学生はスマホの下のタブの3つ目に入る（先頭3つがタブになる）
  { to: '/messages',     icon: 'message', label: 'メッセージ',       roles: ['student'], msgFeature: 'chat' },
  { to: '/notices',      icon: 'megaphone', label: 'お知らせ',       roles: ['student'], msgFeature: 'notices' },
  // 【2026-09-11】ビーコンの受付開始も同じ画面に置いたので「出席受付」に改名。
  //   パスは /otp のまま（ブックマークやスマホのタブが死なないように）。
  // tab … スマホの下のタブに出す項目（そのロールで tab のものが 3 つ未満なら、先頭から埋める。MobileTabBar.jsx）【2026-11-02】
  { to: '/otp',          icon: 'hash', label: '出席受付',         roles: ['teacher','developer'], tab: true },
  // 【2026-11-02】リアルタイム出席は出席受付にまとめた（受付中の回があるとページの一番上に出る）。/live は /otp へ転送
  // 先生・教務のメッセージは、出席の項目の後ろ（スマホの下のタブは出席の3つのまま）【2026-10-16】
  // 【2026-11-02】お知らせの配信・受付設定・一斉連絡・管理は、メッセージの子の項目にした。
  //   子の項目は to を持つ（授業管理の子は section を持つ）。roles と msgFeature は親と同じように見る。
  //   親のスイッチ（chat）が OFF でも、出せる子があれば親を出し、親を押すと最初の子へ行く（getMenuItems）
  { to: '/messages',     icon: 'message', label: 'メッセージ',       roles: ['teacher','academic_staff','developer'], msgFeature: 'chat',
    children: [
      { to: '/messages/announcements', label: 'お知らせの配信',       roles: ['teacher'], msgFeature: 'announcements' },
      { to: '/messages/settings',      label: 'メッセージの受付設定', roles: ['teacher'], msgFeature: 'teacher_dm' },
      { to: '/messages/bulletins',     label: '一斉連絡',             roles: ['academic_staff','developer'], msgFeature: 'bulletins' },
      { to: '/messages/admin',         label: 'メッセージの管理',     roles: ['developer'] },
    ] },
  // 【2026-09-04】出席記録・レポート出力・出席CSV取り込みは、どれも
  //   「授業を選んでから使う」画面だったので授業管理のサブページに統合した。
  //   /classes → 授業を開く → 左のサブメニューから辿る。
  // children を持つ項目は、その配下のページを開いているとき
  // サイドバー内に字下げしたサブメニューを出す（ConoHaのネットワーク配下と同じ形）
  // 授業を1つ開いていないと行き先が決まらないので、/classes/:classId 配下でのみ表示する
  { to: '/classes',      icon: 'book', label: '授業管理',         roles: ['teacher','developer'],
    children: [
      { section: 'schedule', label: '授業スケジュール' },
      // 受講者名簿は学生管理に統合した【2026-09-17】（/classes/:id/roster は学生管理へ転送）
      // 出席記録 = 集計 + レポート出力 + 出席CSV取り込み を1ページにまとめたもの
      //   【2026-10-13】全画面のページに作り直した（サイドバーは画面の左端で出る。pages/AttendanceWorkspace.jsx）
      // 【2026-11-02】出席記録（summary）は親の項目「出席集計」に上げた（下の MENU の次の項目）
      // 出欠を直す専用のページ【2026-10-07】。学生からの申請・確認待ちの打刻もここ
      { section: 'edit',     label: '出欠の確認・修正' },
      // 名簿の取り込み履歴も授業管理の下に入れた【2026-11-02】（教務は親の項目のまま。下の /roster-imports）
      { to: '/roster-imports', label: '名簿取り込み履歴' },
      // 年度の切り替え（新しい年度の授業に、前の年度の時間割を写す）【2026-11-03】
      { to: '/year-rollover', label: '年度の切り替え' },
    ] },
  // 【2026-11-02】出席集計を授業管理の子から親の項目に上げた。
  //   URL は /classes/:id/summary のまま（ブックマーク・画面内のリンクを壊さない）。
  //   classSection を持つ項目は、行き先の授業を Sidebar が「今開いている授業 → 直前に開いた授業 → すべての授業」で決める。
  //   to はキーとページ名の解決用の既定値（授業を一度も開いていないときの行き先）。
  { to: '/classes/all/summary', icon: 'bar-chart', label: '出席集計', roles: ['teacher','developer'],
    classSection: 'summary', tab: true },
  // 【2026-09-28】学生管理は教務だけのメニューにした。教員は授業から名簿を開く
  //   （/students?classId=… のリンクは教員もそのまま使える。ルートは閉じていない）
  { to: '/students',     icon: 'users', label: '学生管理',         roles: ['academic_staff','developer'] },
  // 教務向けの一括インポート（ユーザー一括登録・複数科目の名簿まとめ取り込み）は
  // 授業単位ではないので、教務のサイドバーには残す。
  { to: '/csv-import',   icon: 'folder', label: 'CSVインポート',    roles: ['academic_staff','developer'] },
  { to: '/academic',     icon: 'grad-cap', label: '教務管理',         roles: ['academic_staff','developer'] },
  // 【2026-09-17】変更履歴（/users-audit）は普段見ないので、サイドバーから外した。
  //   入口はアカウント設定の「その他」と、学生管理の下の小さなリンク。
  // 【2026-09-14】名簿の取り込み履歴。年度・学期は取り込み1回ぶんで持っているので、
  //   選び間違えたときの直し場所と、取り消しの入口をここに置く。
  // 【2026-09-30】学生からの出欠の訂正・公欠の申請。先生は担当の授業、教務は大学の授業。
  // 【2026-10-07】先生は授業管理の「出欠の確認・修正」にまとめたので、このメニューは教務だけ（/requests の URL は先生も使える）
  { to: '/requests',     icon: 'list', label: '出欠の確認',       roles: ['academic_staff','developer'] },
  { to: '/roster-imports', icon: 'folder', label: '名簿取り込み履歴', roles: ['academic_staff'] },
  // 【2026-10-16】先生: 授業のお知らせの配信・メッセージの受付設定と定型文。教務: 大学からの一斉連絡。開発者: 通報・機能スイッチ
  //   【2026-11-02】この4つはメッセージの子の項目に移した（上の /messages の children）
  // 【2026-09-10】サーバーの安全確認。専門知識なしで状態を見て、
  //   ロックされたアカウントをその場で解除できる画面。
  //   設定値やディスク残量まで出るので、教務スタッフ以上に限定する。
  { to: '/security',     icon: 'shield', label: '安全確認',         roles: ['academic_staff','developer'] },
  // 【2026-09-17】終わった年度（学期）の締め・暗号化保管・消去。教務だけ。
  { to: '/year-rollover', icon: 'calendar', label: '年度の切り替え', roles: ['academic_staff','developer'] },
  { to: '/data-retention', icon: 'archive', label: '年度の締め・保管', roles: ['academic_staff','developer'] },
  // 【2026-11-02】先生・教務・開発者は、スマホの画面（メニュー）でだけ項目として出す。
  //   PC ではめったに使わないので、サイドバーの下の小さなリンクにした（Sidebar.jsx の mobileOnlyFor）
  { to: '/app-download', icon: 'smartphone', label: 'アプリDL',         roles: ['student','teacher','academic_staff','developer'],
    mobileOnlyFor: ['teacher','academic_staff','developer'] }
];

// サイドバーには出さないが、直接URLで開いたときにページ名を出すためのもの
const HIDDEN_TOP_PAGES = [
  // 【2026-09-07】ICカードは今の運用で使っていないのでサイドバーから外した。
  //   ページとルートはそのまま残してあるので、使うときはここの1行を
  //   MENU 側へ戻せば元通りになる。
  { to: '/cards',         label: 'ICカード' },
  { to: '/class-summary', label: '出席記録' },
  { to: '/reports',       label: 'レポート出力' },
  { to: '/notifications', label: '通知' },
  { to: '/profile',       label: 'アカウント設定' },
  { to: '/users-audit',   label: '変更履歴' },
  // 教員は授業の名簿リンクから開く（サイドバーには出さない）【2026-09-28】
  { to: '/students',      label: '学生管理' },
  { to: '/messages',      label: 'メッセージ' },
  { to: '/messages/settings', label: 'メッセージの受付設定' },
  { to: '/messages/announcements', label: 'お知らせの配信' },
  { to: '/messages/bulletins', label: '一斉連絡' },
  { to: '/messages/admin', label: 'メッセージの管理' },
  { to: '/notices',       label: 'お知らせ' },
  { to: '/app-download',  label: 'アプリDL' },
  { to: '/roster-imports', label: '名簿取り込み履歴' }
];

// 下の階層のページ名（前方一致で引く）【2026-10-08】
const SUB_PAGES = [
  { to: '/classes', label: '授業管理' },
  { to: '/year-rollover', label: '年度の切り替え' },   // 先生は授業管理の子の項目【2026-11-03】
];

/** /classes/:id と /classes/:id/summary（古い export / import も）は出席集計【2026-11-02】 */
export function isClassSummaryPath(pathname) {
  return /^\/classes\/[^/]+(\/(summary|export|import))?\/?$/.test(String(pathname || ''));
}

/**
 * サイドバーに出すメニュー項目を返す。
 * @param {{ role: string, features?: string[] }} user
 */
export function getMenuItems(user) {
  if (!user || !user.role) return [];
  const out = [];
  for (const it of MENU) {
    if (!it.roles.includes(user.role)) continue;
    // 子の項目（to を持つもの）は親と同じ条件で絞る【2026-11-02】
    const kids = (it.children && it.children.some(ch => ch.to))
      ? it.children.filter(ch => isVisible(ch, user))
      : it.children;
    if (isVisible(it, user)) {
      out.push(kids === it.children ? it : { ...it, children: kids });
    } else if (kids && kids !== it.children && kids.length > 0) {
      // 親のスイッチは OFF でも子は出せる → 親を押すと最初の子へ（href）
      out.push({ ...it, children: kids, href: kids[0].to });
    }
  }
  return out;
}

/** 1つの項目を出すかどうか（ロール・機能フラグ・メッセージ機能のスイッチ） */
function isVisible(it, user) {
  if (it.roles && !it.roles.includes(user.role)) return false;
  // feature が指定されている場合、user.features が配列ならその値を含むことを要求
  // user.features が undefined / null の場合は「全機能ON」として通す（後方互換）
  if (it.feature && Array.isArray(user.features) && !user.features.includes(it.feature)) {
    return false;
  }
  // メッセージ機能のスイッチ（Layout が /api/messages/features を読んで user.msgFeatures に入れる）。
  //   まだ読めていない（undefined）ときは出さない
  if (it.msgFeature) {
    const f = user.msgFeatures;
    if (!f) return false;
    if (it.msgFeature === 'chat') { if (!(f.teacher_dm || f.student_chat || f.class_chat)) return false; }
    else if (it.msgFeature === 'notices') { if (!(f.announcements || f.bulletins)) return false; }
    else if (!f[it.msgFeature]) return false;
  }
  return true;
}

/**
 * 現在のパスに対応するページタイトルを返す。
 * 通知・プロフィールも含めて解決する。
 */
export function getPageLabel(pathname, user) {
  // 授業の出席集計（/classes/:id、/classes/:id/summary と古い export / import）【2026-11-02】
  if (isClassSummaryPath(pathname)) return '出席集計';
  const all = [...getMenuItems(user), ...HIDDEN_TOP_PAGES, ...SUB_PAGES];
  const hit = all.find(it => it.to === pathname);
  if (hit) return hit.label;
  // 完全に一致しないとき（/classes/21/summary など）は、いちばん長く前が一致するもの【2026-10-08 レビュー】
  const p = String(pathname || '').replace(/\/+$/, '');
  const pre = all
    .filter(it => it.to && it.to !== '/' && p.startsWith(it.to.replace(/\/+$/, '') + '/'))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return pre ? pre.label : '';
}
